import { FC } from 'react';
import Drawer from 'components/Drawer/Drawer';
import TextField from 'components/TextField/TextField';
import { useForm } from 'react-hook-form';
import IconHelper from '../EditOrCreateUser/IconHelper/IconHelper';
import CommonButton from 'components/CommonButton/CommonButton';
import { Switch } from '@headlessui/react';
import { ClinicUser } from 'interfaces/clinicUser';

interface EditUserDrawerProps {
  isShowing: boolean;
  onClose: () => void;
  clinicUser: ClinicUser;
  isRoleSectionRendered?: boolean;
  onSubmit: (data: EditUserDrawerForm) => void;
}

export interface EditUserDrawerForm {
  email: string;
  firstName: string;
  lastName: string;
  role: string;
  isSchedulingError: boolean;
  isApptRequest: boolean;
  isApptConfirmation: boolean;
  isApptCancellation: boolean;
}

const NOTIFICATIONS = [
  { title: 'Scheduling Error Notifications', key: 'isSchedulingError' },
  { title: 'Appt Request Email', key: 'isApptRequest' },
  { title: 'Appt Confirmation Email', key: 'isApptConfirmation' },
  { title: 'Appt Rejected Email', key: 'isApptCancellation' },
];

const EditUserDrawer: FC<EditUserDrawerProps> = ({
  isShowing,
  onClose,
  clinicUser,
  isRoleSectionRendered = false,
  onSubmit,
}) => {
  const { control, register, setValue, watch, formState, handleSubmit, reset } =
    useForm({
      mode: 'all',
      defaultValues: {
        email: clinicUser.clinicUser.email,
        firstName: clinicUser.firstName,
        lastName: clinicUser.lastName,
        role: clinicUser.role,
        isSchedulingError: clinicUser.clinicUser.notification.isSchedulingError,
        isApptRequest: clinicUser.clinicUser.notification.isApptRequest,
        isApptConfirmation:
          clinicUser.clinicUser.notification.isApptConfirmation,
        isApptCancellation:
          clinicUser.clinicUser.notification.isApptCancellation,
      },
    });

  const { isValid, isDirty, isSubmitting } = formState;

  const onDrawerClose = () => {
    reset();
    onClose();
  };

  return (
    <Drawer
      title={'Edit User'}
      isShowing={isShowing}
      setIsShowing={onDrawerClose}
      footer={
        <div className="w-full h-6 flex px-2.4 justify-end items-center shadow-primary sticky bottom-0 bg-white gap-x-1.6">
          <CommonButton
            className="!min-w-[12rem]"
            isLoading={isSubmitting}
            disabled={!isValid || !isDirty}
            onClick={handleSubmit(onSubmit)}
          >
            Update
          </CommonButton>
        </div>
      }
    >
      <form className="flex flex-col gap-y-2.4 text-14 leading-[2.1rem]">
        <TextField
          id="email"
          control={control}
          label="Email Address"
          placeholder="Input Email Address"
          disabled={true}
          iconHelper={<IconHelper />}
        />
        <TextField
          id="firstName"
          control={control}
          label="First Name"
          placeholder="Input First Name"
          rules={{
            required: 'This field is required',
          }}
        />
        <TextField
          id="lastName"
          control={control}
          label="Last Name"
          placeholder="Input Last Name"
          rules={{
            required: 'This field is required',
          }}
        />
        {isRoleSectionRendered && (
          <fieldset>
            <legend className="text-darkest-grey text-16 leading-[2.4rem] font-bold">
              Select Role
            </legend>
            <div className="mt-1.7 flex gap-x-[11.1rem]">
              <div className="flex items-center gap-x-0.9">
                <input
                  id="radioUser"
                  type="radio"
                  className="w-2.2 h-2.2"
                  value="practice_user"
                  {...register('role', { required: true })}
                />
                <label htmlFor="radioUser">User</label>
              </div>
              <div className="flex items-center gap-x-0.9">
                <input
                  id="radioAdmin"
                  type="radio"
                  className="w-2.2 h-2.2"
                  value="practice_admin"
                  {...register('role', { required: true })}
                />
                <label htmlFor="radioAdmin">Admin</label>
              </div>
            </div>
          </fieldset>
        )}
        <div className="border-t border-light-grey">
          <span className="font-bold pt-2.4 pb-1.2 flex">Notifications</span>
          <div className="flex flex-col gap-y-1.2">
            {NOTIFICATIONS.map((item) => {
              const key =
                item.key as keyof EditUserDrawerProps['clinicUser']['clinicUser']['notification'];

              const value = watch(key);

              return (
                <div className="flex items-center justify-between">
                  <span>{item.title}</span>
                  <Switch
                    checked={value}
                    onChange={() =>
                      setValue(key, !value, {
                        shouldValidate: true,
                        shouldDirty: true,
                      })
                    }
                    className={`${
                      value ? 'bg-magenta' : 'bg-[#78788029]'
                    } relative inline-flex h-2.4 w-4.5 items-center rounded-full`}
                  >
                    <span
                      className={`${
                        value ? 'translate-x-[2.3rem]' : 'translate-x-[2px]'
                      } inline-block w-2 h-2 transform rounded-full transition bg-white`}
                    />
                  </Switch>
                </div>
              );
            })}
          </div>
        </div>
      </form>
    </Drawer>
  );
};

export default EditUserDrawer;
